import { useRecoilState, useSetRecoilState } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import campaignDateRangeIDAtom from "recoil/stats/campaignDateRangeID";
import { dashboardCampaignSelectionAtom } from "recoil/stats/dashboardFilters";
import useCampaignService from "services/campaign";
import { CAMPAIGNS_KEY_PREFIX, campaignsSummaryKey } from "hooks/useCampaignsSummary";
import { campaignDetailKey } from "hooks/useCampaignDetail";

const useCampaigns = () => {
    const campaignService = useCampaignService();
    const queryClient = useQueryClient();
    const [dateRangeID, setDateRangeID] = useRecoilState(campaignDateRangeIDAtom);
    const setDashboardCampaignSelection = useSetRecoilState(dashboardCampaignSelectionAtom);

    const addNewCampaign = useMutation(
        (data) => campaignService.createCampaign(data).then(r => r.data),
        {
            onSuccess: (newCampaign) => {
                const newID = newCampaign.CampaignID;
                queryClient.setQueryData(campaignsSummaryKey(dateRangeID), prev =>
                    prev
                        ? {
                            ...prev,
                            StatsSummaries: [
                                ...prev.StatsSummaries,
                                { CampaignDetail: newCampaign, Stats: { Traffic: 0, Joins: 0, Upgrades: 0, Revenue: 0 } },
                            ],
                          }
                        : prev
                );
                queryClient.invalidateQueries(CAMPAIGNS_KEY_PREFIX);
                queryClient.setQueryData(
                    campaignDetailKey(newID, dateRangeID),
                    { CampaignDetail: newCampaign, Stats: [] }
                );
                setDashboardCampaignSelection(newID);
            },
        }
    );

    const deleteCampaign = useMutation(
        (id) => campaignService.hideCampaign(id).then(r => {
            if (!r.data.Result) throw new Error("Failed to remove this campaign");
            return true;
        }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(CAMPAIGNS_KEY_PREFIX);
            },
        }
    );

    const updateCampaignName = useMutation(
        ({ id, title }) => campaignService.updateTitle(id, title).then(r => {
            if (!r.data) throw new Error("Failed to add promo code");
            return r.data;
        }),
        {
            onSuccess: (campaign, { id }) => {
                queryClient.setQueryData(
                    campaignDetailKey(parseInt(id), dateRangeID),
                    prev => prev
                        ? { ...prev, CampaignDetail: campaign }
                        : prev
                );
            },
        }
    );

    // Variables are passed as a single object { id, code } because useMutation
    // only supports one argument. id is kept as-is (string from useParams) so
    // onSuccess can parseInt it when building the cache key.
    const addPromoCode = useMutation(
        ({ id, code }) => campaignService.createPromoCode(id, code).then(r => {
            if (!r.data) throw new Error("Failed to add promo code");
            return r.data;
        }),
        {
            onSuccess: (promoCode, { id }) => {
                queryClient.setQueryData(
                    campaignDetailKey(parseInt(id), dateRangeID),
                    prev => prev
                        ? { ...prev, CampaignDetail: { ...prev.CampaignDetail, PromoCode: promoCode } }
                        : prev
                );
            },
        }
    );

    const extendPromoCode = useMutation(
        ({ id }) => campaignService.extendPromoCode(id).then(r => {
            if (!r.data) throw new Error("Failed to extend promo code");
            return r.data;
        }),
        {
            onSuccess: (promoCode, { id }) => {
                queryClient.setQueryData(
                    campaignDetailKey(parseInt(id), dateRangeID),
                    prev => prev
                        ? { ...prev, CampaignDetail: { ...prev.CampaignDetail, PromoCode: promoCode } }
                        : prev
                );
            },
        }
    );

    const updatePromoCodeName = useMutation(
        ({ id, code }) => campaignService.updatePromoCodeName(id, code).then(r => {
            if (!r.data) throw new Error("Failed to update promo code");
            return r.data;
        }),
        {
            onSuccess: (promoCode, { id }) => {
                queryClient.setQueryData(
                    campaignDetailKey(parseInt(id), dateRangeID),
                    prev => prev
                        ? { ...prev, CampaignDetail: { ...prev.CampaignDetail, PromoCode: promoCode } }
                        : prev
                );
            },
        }
    );

    return {
        dateRangeID,
        setDateRangeID,
        addNewCampaign,
        deleteCampaign,
        addPromoCode,
        extendPromoCode,
        updateCampaignName,
        updatePromoCodeName
    };
};

export default useCampaigns;
